import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    BillToModel,
    BrandModel,
    CartLineModel,
    CartModel,
    OrderLineModel,
    ProductModel,
} from "@insite/client-framework/Types/ApiModels";

declare global {
    interface Window {
        dataLayer: [{}];
    }
}

const windowExist = typeof window !== "undefined";
const productIndex = 3;

export const push = (data: object, needsTimeout?: boolean) => {
    const doPush = (data: object) => {
        if (windowExist && Object.prototype.hasOwnProperty.call(window, "dataLayer")) {
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push(data);
        }
        // Use this locally
        else {
            global.console.log("AnalyticsService:", data);
        }
    };

    if (needsTimeout) {
        // setTimeout to avoid racing conflicts with built-in events
        setTimeout(() => {
            doPush(data);
        }, 120);
    } else {
        doPush(data);
    }
};

const isCartLine = function (
    productObject: CartLineModel | ProductContextModel | OrderLineModel,
): productObject is CartLineModel {
    return (productObject as CartLineModel).line !== undefined;
};

const isProductContext = function (
    productObject: CartLineModel | ProductContextModel | OrderLineModel,
): productObject is ProductContextModel {
    return (
        (productObject as CartLineModel).line === undefined &&
        (productObject as OrderLineModel).lineNumber === undefined
    );
};

const isOrderLine = function (
    productObject: CartLineModel | ProductContextModel | OrderLineModel,
): productObject is ProductContextModel {
    return (productObject as OrderLineModel).lineNumber !== undefined;
};

export const resolveCategory = () => {
    if (windowExist) {
        const resolvedCategory = window.location.pathname
            .slice(1)
            .replace(/-/gu, " ")
            .split("/")
            .filter((category, i) => i !== 0);
        if (resolvedCategory.length === productIndex) {
            resolvedCategory.pop();
        }
        return resolvedCategory.join("/");
    }
};

export const resolveVariant = (title: any) => title.replace(/[sS]*.*( - )/, "");

const formatPrice = (price: number) => price?.toFixed(2) ?? "";

const mapCartLines = (cartLines: CartLineModel[]) => {
    return cartLines?.map((cartLine: CartLineModel) => {
        const price = cartLine.pricing?.unitNetPrice.toFixed(2);
        return {
            name: cartLine.shortDescription,
            id: cartLine.erpNumber,
            ...(price ? { price } : {}),
            quantity: cartLine.qtyOrdered,
            variant: resolveVariant(cartLine.shortDescription),
        };
    });
};

const mapCartLinesFromCartModel = (cart: any) => {
    const cartLines: CartLineModel[] = cart.cartLines;
    return mapCartLines(cartLines);
};

const mapProductContext = (
    product: ProductModel,
    productInfo: ProductInfo,
    listName?: string | null | undefined,
): object => {
    const priceModel = productInfo.pricing?.unitNetPrice ?? 0;
    let brand = product.brand ?? "";
    if (typeof brand !== "string" && product.brand?.name) {
        brand = product.brand?.name;
    }
    const dataObj: any = {
        name: product.productTitle,
        id: product.productNumber,
        brand,
        price: formatPrice(priceModel),
        quantity: productInfo.qtyOrdered,
        category: resolveCategory(),
    };
    if (listName) {
        dataObj.list = listName || "";
    }
    return dataObj;
};

const mapOrderLineModel = (orderLine: OrderLineModel, forcedQuantity?: number): object => {
    const priceModel = orderLine.unitNetPrice ?? 0;
    let brand = orderLine.brand ?? "";
    if (typeof brand !== "string" && orderLine.brand?.name) {
        brand = orderLine.brand?.name;
    }

    return {
        name: orderLine.shortDescription,
        id: orderLine.productId,
        brand,
        price: formatPrice(priceModel),
        quantity: forcedQuantity || orderLine.qtyOrdered,
        category: resolveCategory(),
    };
};

const mapCartLine = (cartLine: CartLineModel, forcedQuantity?: number): object => {
    const priceModel = cartLine.pricing?.unitNetPrice ?? 0;
    let brand = cartLine.brand ?? "";
    if (typeof brand !== "string" && cartLine.brand?.name) {
        brand = cartLine.brand?.name;
    }

    return {
        name: cartLine.shortDescription,
        id: cartLine.erpNumber,
        brand,
        price: formatPrice(priceModel),
        quantity: forcedQuantity || cartLine.qtyOrdered,
        category: resolveCategory(),
    };
};

const mapProductImpression = (products: ProductModel[] | undefined, pageType: string) =>
    products?.map((product, position) => ({
        name: product.productTitle,
        id: product.productNumber,
        price: formatPrice(product.unitListPrice),
        category: resolveCategory(),
        list: pageType,
        position: position + 1,
    }));

export const dispatchProductClickEvent = (
    productObject: CartLineModel | ProductContextModel | OrderLineModel,
    listName?: string | null | undefined,
) => {
    let theProduct;
    if (isCartLine(productObject)) {
        theProduct = mapCartLine(productObject);
    } else if (isProductContext(productObject)) {
        const { product, productInfo } = productObject;
        theProduct = listName
            ? mapProductContext(product, productInfo, listName)
            : mapProductContext(product, productInfo);
    } else if (isOrderLine(productObject)) {
        theProduct = mapOrderLineModel(productObject);
    }
    const event: any = {
        event: "productClick",
        ecommerce: {
            click: {
                products: [theProduct],
            },
        },
    };
    if (listName) {
        event.ecommerce.click.actionField = { list: listName };
    }
    push(event);
};

export const dispatchCheckoutEvent = (stepNumber: number, cart: CartModel, optionString?: string) => {
    const actionField: any = { step: stepNumber, action: "checkout" };
    if (optionString) {
        actionField.option = optionString;
    }
    push(
        {
            event: "checkout",
            ecommerce: {
                checkout: {
                    actionField,
                    products: mapCartLinesFromCartModel(cart) ?? [],
                },
            },
        },
        true,
    );
};

// Can be dispatched after regular dispatchCheckoutEvent to add additional data
export const dispatchCheckoutOptionEvent = (stepNumber: number, optionString: string) => {
    push(
        {
            event: "checkoutOption",
            ecommerce: {
                checkout_option: {
                    actionField: { step: stepNumber, option: optionString },
                },
            },
        },
        true,
    );
};

export const dispatchProductDetailView = (product: ProductModel, productInfo: ProductInfo) => {
    push(
        {
            event: "productDetail",
            ecommerce: {
                detail: {
                    products: [mapProductContext(product, productInfo)],
                },
            },
        },
        true,
    );
};

export const dispatchAddToCartEvent = (
    productContext: ProductContextModel,
    currency: string | undefined,
    listName?: string | null | undefined,
) => {
    const { product, productInfo } = productContext;
    const event: any = {
        event: "addToCart",
        ecommerce: {
            currencyCode: currency,
            add: {
                products: [mapProductContext(product, productInfo, listName || undefined)],
            },
        },
    };
    if (listName) {
        event.ecommerce.actionField = { list: listName };
    }
    push(event, true);
};

export const dispatchRemoveFromCartEvent = (cartLine: CartLineModel, currency: string | undefined) => {
    push(
        {
            event: "removeFromCart",
            ecommerce: {
                currencyCode: currency,
                remove: {
                    products: [mapCartLine(cartLine)],
                },
            },
        },
        true,
    );
};

export const dispatchCartLineQuantityChangeEvent = (
    cartLine: CartLineModel,
    newQty: number,
    oldQty: number,
    currency: string | undefined,
) => {
    if (newQty > oldQty) {
        push(
            {
                event: "addToCart",
                ecommerce: {
                    currencyCode: currency,
                    add: {
                        products: [mapCartLine(cartLine, newQty - oldQty)],
                    },
                },
            },
            true,
        );
    } else {
        push({
            event: "removeFromCart",
            ecommerce: {
                currencyCode: currency,
                remove: {
                    products: [mapCartLine(cartLine, oldQty - newQty)],
                },
            },
        });
    }
};

export const dispatchPurchaseEvent = (orderNumber: string, cart: CartModel, billTo?: BillToModel, coupon?: string) => {
    push(
        {
            event: "purchase",
            ecommerce: {
                purchase: {
                    actionField: {
                        id: orderNumber,
                        affiliation: billTo?.customerName ?? "Online Store",
                        revenue: formatPrice(cart.orderSubTotal),
                        tax: formatPrice(cart.totalTax),
                        shipping: formatPrice(cart.shippingAndHandling),
                        coupon: cart.promotionCode ? cart.promotionCode : coupon,
                    },
                    products: mapCartLinesFromCartModel(cart) ?? [],
                },
            },
        },
        true,
    );
};

export const dispatchProductImpressions = (
    products: ProductModel[] | undefined,
    currency: string | undefined,
    pageType: string,
) => {
    if (products?.length === 0) {
        return;
    }

    push(
        {
            event: "productImpression",
            ecommerce: {
                currencyCode: currency,
                actionField: { list: pageType },
                impressions: mapProductImpression(products, pageType),
            },
        },
        true,
    );
};

export const dispatchContactUsEvent = (formProperties: any) => {
    const props = formProperties.apiParameter;
    push(
        {
            event: "contactUsFormSubmit",
            formCompany: props.company ? props.company : "",
            formTopic: props.topic ? props.topic : "",
            formCountry: props.country ? props.country : "",
            formCity: props.city ? props.city : "",
            formState: props.state ? props.state : "",
        },
        true,
    );
};

export const dispatchQuickOrderAddProduct = (origin?: string) => {
    push(
        {
            event: "add_product_to_quick_order",
            feature: "Quick order",
            eventCategory: "Quick order",
            eventAction: "Add products",
            eventLabel: origin || "List", // 'Manual' or 'List'
        },
        true,
    );
};

export const dispatchQuickOrderRemoveProduct = () => {
    push({
        event: "product_removed_from_quick_order",
        feature: "Quick order",
        eventCategory: "Quick order",
        eventAction: "Remove products",
    });
};

export const dispatchQuickOrderUploadFile = () => {
    push({
        event: "quick_order_list_items_uploaded",
        feature: "Quick order",
        eventCategory: "Quick order",
        eventAction: "Upload List",
        eventLabel: "Upload List",
    });
};

export const dispatchQuickOrderDownloadTemplate = () => {
    push({
        event: "quick_order_template_downloaded",
        eventCategory: "Quick order",
        eventAction: "Template download",
        eventLabel: "Template download",
    });
};

export const dispatchQuickOrderCancelUpload = () => {
    push({
        event: "cancel_quick_order_list_upload",
        feature: "Quick order",
        eventCategory: "Quick order",
        eventAction: "Cancel List Upload",
        eventLabel: "Cancel Upload",
    });
};

export const dispatchQuickOrderStartUpload = () => {
    push({
        event: "start_quick_order_list_upload",
        eventCategory: "Quick order",
        eventAction: "Start List Upload",
        eventLabel: "Start Upload",
    });
};

export const dispatchQuickOrderErrorUpload = () => {
    push({
        event: "error_quick_order_list_upload",
        eventCategory: "Quick order",
        eventAction: "Start List Upload",
        eventLabel: "Start Upload",
    });
};

export const dispatchQuickOrderSwapPart = (
    initialProduct: ProductModel,
    initialProductInfo: ProductInfo,
    newProduct: ProductModel,
    newProductInfo: ProductInfo,
) => {
    const iniProd = mapProductContext(initialProduct, initialProductInfo);
    const newProd = mapProductContext(newProduct, newProductInfo);
    push({
        event: "product_swapped",
        feature: "Quick order",
        eventCategory: "Quick Order",
        eventAction: "Swap product",
        initialProduct: iniProd,
        newProduct: newProd,
    });
};

export const dispatchQuickOrderAddAllItemsToCheckout = (lines: CartLineModel[]) => {
    push({
        event: "add_all_items_and_checkout",
        feature: "Quick order",
        eventCategory: "Quick order",
        eventAction: "Add items and checkout",
    });

    if (lines) {
        push({
            event: "addToCart",
            ecommerce: {
                currencyCode: "cad",
                add: {
                    products: [mapCartLines(lines)],
                },
            },
        });
    }
};

export const dispatchQuickOrderLookedAtAlternatives = () => {
    push({
        event: "looked_at_alternatives",
        feature: "Quick order",
        eventCategory: "Quick order",
        eventAction: "Open alternative parts",
    });
};

export const dispatchQuickOrderQtyChange = (increase: boolean) => {
    push({
        event: "quantity_changed_in_quick_order",
        feature: "Quick order",
        eventCategory: "Quick order",
        eventAction: "change quantity",
        eventLabel: increase ? "increase" : "decrease",
    });
};
